import { Flex, Icon, Box } from "@chakra-ui/react";
import CDS from "design-system";
import { MdCheckCircle, MdRadioButtonUnchecked, MdErrorOutline } from "react-icons/md";

interface CheckpointTask {
  id: number;
  name: string;
  status: string;
  dueAt?: string;
  assigneeId?: number;
}

function isOverdue(task: CheckpointTask) {
  return (
    task.dueAt &&
    new Date(task.dueAt) < new Date() &&
    task.status !== "completed" &&
    task.status !== "skipped" &&
    task.status !== "removed" &&
    task.status !== "closed_stale"
  );
}

function taskIcon(task: CheckpointTask) {
  if (task.status === "completed") return { icon: MdCheckCircle, color: "green.500" };
  if (isOverdue(task)) return { icon: MdErrorOutline, color: "red.500" };
  return { icon: MdRadioButtonUnchecked, color: "gray.400" };
}

interface CheckpointTaskListProps {
  results: { tasks?: CheckpointTask[] } | null | undefined;
  compact?: boolean;
}

export default function CheckpointTaskList({ results, compact = false }: CheckpointTaskListProps) {
  const tasks = results?.tasks;
  if (!tasks || tasks.length === 0) return null;

  const sorted = [...tasks].sort((a, b) => {
    if (a.status === "completed" && b.status !== "completed") return 1;
    if (a.status !== "completed" && b.status === "completed") return -1;
    const aOverdue = isOverdue(a);
    const bOverdue = isOverdue(b);
    if (aOverdue && !bOverdue) return -1;
    if (!aOverdue && bOverdue) return 1;
    return 0;
  });

  return (
    <Box>
      <CDS.Text10 fontWeight="600" color="content.tertiary" mb="2">
        Tasks ({tasks.length})
      </CDS.Text10>
      <Flex direction="column" gap={compact ? "0" : "1"}>
        {sorted.map((task) => {
          const { icon, color } = taskIcon(task);
          const overdue = isOverdue(task);
          return (
            <Flex
              key={task.id}
              align="center"
              gap="2"
              py={compact ? "1" : "1.5"}
              px="2"
              borderRadius="md"
              _hover={{ bg: "gray.50" }}
            >
              <Icon as={icon} color={color} boxSize={compact ? "3.5" : "4"} flexShrink={0} />
              <CDS.Text14
                fontSize={compact ? "xs" : "sm"}
                color={task.status === "completed" ? "content.tertiary" : "content.primary"}
                textDecoration={task.status === "completed" ? "line-through" : "none"}
                noOfLines={1}
                flex="1"
              >
                {task.name}
              </CDS.Text14>
              {overdue && (
                <CDS.Text10 fontSize="2xs" color="red.500" fontWeight="600" flexShrink={0}>
                  OVERDUE
                </CDS.Text10>
              )}
            </Flex>
          );
        })}
      </Flex>
    </Box>
  );
}
